import { Chip } from "@mui/material";
import moment from "moment";
import {
  GovItem,
  GovKind,
  GovPriority,
  GovSettingsOn,
  LinkRelation,
  OverallStatus,
  Party,
  ReportStatus,
  SignOffStatus,
} from "../../../utils/icGovernance";
import { SUPPORT_BASE } from "../Support/supportView";

/**
 * Shared labels, chip tones, tabs and small formatters for the governance
 * screens (`Dashboard/Governance/`). Labels are the stored status in words
 * (matching the exported spreadsheets), never Support's friendlier wording.
 * Contract: thrive_server/claude_doc/governance/CONTRACT.md.
 */

export const PRIMARY = "#9E1F63FF";
export const CARD_BORDER = "1px solid #ececf0";
// The workbook lives on the Support page (`/dashboard/support`, admins with
// governance on). Items sit directly under it by display id (TD-CR-4) — the
// same URL a Support ticket has; tab names are static segments and rank first.
export { SUPPORT_BASE };

export const itemUrl = (item: Pick<GovItem, "displayId" | "id">) =>
  `${SUPPORT_BASE}/${encodeURIComponent(item.displayId || item.id)}`;
export const itemUrlById = (displayIdOrId: string) =>
  `${SUPPORT_BASE}/${encodeURIComponent(displayIdOrId)}`;
export const reportUrl = (id: string) =>
  `${SUPPORT_BASE}/weekly-status/${encodeURIComponent(id)}`;

// ---------------------------------------------------------------------------
// Tabs — named and ordered like the client's workbook (user decision 2026-10-10)
// ---------------------------------------------------------------------------

export type GovTabKey = "tracker" | "change-requests" | "issues" | "releases" | "uat" | "weekly-status";

export interface GovTab {
  key: GovTabKey;
  label: string;
  path: string;
  /** Item kind listed on this tab (undefined = weekly reports). */
  kind?: GovKind;
  /** Settings module that must be on for the tab to show (undefined = always). */
  module?: "tasks" | "changeRequests" | "releases" | "uat" | "weeklyStatus";
}

export const GOV_TABS: GovTab[] = [
  { key: "tracker", label: "Master Project Tracker", path: `${SUPPORT_BASE}/tracker`, kind: "TASK", module: "tasks" },
  { key: "change-requests", label: "Change Request", path: `${SUPPORT_BASE}/change-requests`, kind: "CHANGE_REQUEST", module: "changeRequests" },
  { key: "issues", label: "Issue and Bug Log", path: `${SUPPORT_BASE}/issues`, kind: "ISSUE" },
  { key: "releases", label: "Release and Update Plan", path: `${SUPPORT_BASE}/releases`, kind: "RELEASE", module: "releases" },
  { key: "uat", label: "UAT and Sign-Off", path: `${SUPPORT_BASE}/uat`, kind: "UAT_TEST", module: "uat" },
  { key: "weekly-status", label: "Weekly Project Status", path: `${SUPPORT_BASE}/weekly-status`, module: "weeklyStatus" },
];

/** The tabs whose module is on for this project. */
export const visibleTabs = (settings: GovSettingsOn | null | undefined): GovTab[] =>
  GOV_TABS.filter((t) => !t.module || !settings || settings.modules?.[t.module] !== false);

export const tabForKind = (kind: GovKind): GovTab =>
  GOV_TABS.find((t) => t.kind === kind) ?? GOV_TABS[2];

// ---------------------------------------------------------------------------
// Kind labels
// ---------------------------------------------------------------------------

export const KIND_LABEL: Record<GovKind, string> = {
  ISSUE: "Issue",
  TASK: "Task",
  CHANGE_REQUEST: "Change request",
  RELEASE: "Release",
  UAT_TEST: "UAT test",
};

export const kindLabel = (kind: GovKind | null | undefined) => (kind ? KIND_LABEL[kind] ?? kind : "—");

// ---------------------------------------------------------------------------
// Statuses — stored names in order per kind, with chip tones
// ---------------------------------------------------------------------------

export type Tone = "blue" | "amber" | "green" | "grey" | "red";

const TONE_COLORS: Record<Tone, { bg: string; fg: string }> = {
  blue: { bg: "#e8f0fe", fg: "#1a56c4" },
  amber: { bg: "#fbf5e6", fg: "#8a6d00" },
  green: { bg: "#e7f5ec", fg: "#1e7b3c" },
  grey: { bg: "#f0f0f3", fg: "#5a5a66" },
  red: { bg: "#fdecea", fg: "#c62828" },
};

export const KIND_STATUSES: Record<GovKind, { status: string; tone: Tone }[]> = {
  ISSUE: [
    { status: "NEW", tone: "blue" },
    { status: "TRIAGED", tone: "blue" },
    { status: "IN_PROGRESS", tone: "amber" },
    { status: "WAITING", tone: "amber" },
    { status: "FIXED", tone: "green" },
    { status: "RETEST", tone: "blue" },
    { status: "CLOSED", tone: "grey" },
    { status: "REJECTED", tone: "grey" },
  ],
  TASK: [
    { status: "NOT_STARTED", tone: "grey" },
    { status: "IN_PROGRESS", tone: "amber" },
    { status: "BLOCKED", tone: "red" },
    { status: "COMPLETE", tone: "green" },
    { status: "SIGNED_OFF", tone: "green" },
  ],
  CHANGE_REQUEST: [
    { status: "SUBMITTED", tone: "blue" },
    { status: "ASSESSING", tone: "blue" },
    { status: "PENDING_DECISION", tone: "amber" },
    { status: "APPROVED", tone: "green" },
    { status: "REJECTED", tone: "grey" },
    { status: "DEFERRED", tone: "grey" },
    { status: "SCHEDULED", tone: "blue" },
    { status: "IMPLEMENTED", tone: "green" },
  ],
  RELEASE: [
    { status: "PLANNED", tone: "grey" },
    { status: "IN_DEV", tone: "amber" },
    { status: "QA", tone: "blue" },
    { status: "UAT", tone: "blue" },
    { status: "APPROVED", tone: "green" },
    { status: "DEPLOYED", tone: "green" },
    { status: "VALIDATED", tone: "green" },
    { status: "ROLLED_BACK", tone: "red" },
  ],
  UAT_TEST: [
    { status: "NOT_RUN", tone: "grey" },
    { status: "PASS", tone: "green" },
    { status: "FAIL", tone: "red" },
    { status: "BLOCKED", tone: "red" },
  ],
};

export const statusesFor = (kind: GovKind): string[] => KIND_STATUSES[kind].map((s) => s.status);

/** "PENDING_DECISION" → "Pending Decision"; QA and UAT stay upper-case. */
export const statusLabel = (status: string | null | undefined): string => {
  if (!status) return "—";
  if (status === "QA" || status === "UAT") return status;
  return status
    .toLowerCase()
    .split("_")
    .map((w) => (w === "uat" || w === "qa" ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
};

export const statusTone = (kind: GovKind, status: string): Tone =>
  KIND_STATUSES[kind].find((s) => s.status === status)?.tone ?? "grey";

export const toneColors = (tone: Tone) => TONE_COLORS[tone];

export const StatusChip = ({
  kind,
  status,
  size = "small",
}: {
  kind: GovKind;
  status: string;
  size?: "small" | "medium";
}) => {
  const c = TONE_COLORS[statusTone(kind, status)];
  return (
    <Chip
      size={size}
      label={statusLabel(status)}
      sx={{ fontWeight: 700, backgroundColor: c.bg, color: c.fg }}
    />
  );
};

// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------

const PRIORITY_TONE: Record<GovPriority, { bg: string; fg: string }> = {
  LOW: TONE_COLORS.grey,
  MEDIUM: TONE_COLORS.blue,
  HIGH: { bg: "#fff1e0", fg: "#b45309" },
  CRITICAL: TONE_COLORS.red,
};

export const priorityLabel = (priority: string | null | undefined) =>
  priority ? priority.charAt(0) + priority.slice(1).toLowerCase() : "—";

export const PriorityChip = ({ priority }: { priority: GovPriority | null | undefined }) => {
  const tone = PRIORITY_TONE[(priority ?? "LOW") as GovPriority] ?? PRIORITY_TONE.LOW;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={priorityLabel(priority)}
      sx={{ fontWeight: 600, color: tone.fg, borderColor: tone.fg, backgroundColor: tone.bg }}
    />
  );
};

// ---------------------------------------------------------------------------
// Sign-off, decision, report chips
// ---------------------------------------------------------------------------

export const SIGN_OFF: Record<SignOffStatus, { label: string; tone: Tone }> = {
  NONE: { label: "Not requested", tone: "grey" },
  PENDING: { label: "Pending", tone: "amber" },
  APPROVED: { label: "Approved", tone: "green" },
  REJECTED: { label: "Rejected", tone: "red" },
};

export const SignOffChip = ({ status }: { status: SignOffStatus | null | undefined }) => {
  const s = SIGN_OFF[status ?? "NONE"] ?? SIGN_OFF.NONE;
  const c = TONE_COLORS[s.tone];
  return <Chip size="small" label={s.label} sx={{ fontWeight: 700, backgroundColor: c.bg, color: c.fg }} />;
};

export const decisionTone = (outcome: string | null | undefined): Tone =>
  outcome === "APPROVED" ? "green" : outcome === "REJECTED" || outcome === "DEFERRED" ? "grey" : "amber";

export const DecisionChip = ({ outcome }: { outcome: string | null | undefined }) => {
  const c = TONE_COLORS[decisionTone(outcome)];
  return (
    <Chip
      size="small"
      label={outcome ? statusLabel(outcome) : "Awaiting decision"}
      sx={{ fontWeight: 700, backgroundColor: c.bg, color: c.fg }}
    />
  );
};

export const REPORT_STATUS: Record<ReportStatus, { label: string; tone: Tone }> = {
  PUBLISHED: { label: "Published", tone: "blue" },
  ACKNOWLEDGED: { label: "Acknowledged", tone: "green" },
};

export const ReportStatusChip = ({ status }: { status: ReportStatus }) => {
  const s = REPORT_STATUS[status] ?? { label: statusLabel(status), tone: "grey" as Tone };
  const c = TONE_COLORS[s.tone];
  return <Chip size="small" label={s.label} sx={{ fontWeight: 700, backgroundColor: c.bg, color: c.fg }} />;
};

const OVERALL_COLORS: Record<OverallStatus, string> = {
  GREEN: "#16a34a",
  AMBER: "#d97706",
  RED: "#c62828",
};

/** Solid chip in the report's RAG colour. */
export const OverallStatusChip = ({ status }: { status: OverallStatus }) => (
  <Chip
    size="small"
    label={status}
    sx={{ fontWeight: 800, backgroundColor: OVERALL_COLORS[status] ?? "#5a5a66", color: "white" }}
  />
);

export const UAT_RESULT_LABEL: Record<string, string> = {
  NOT_RUN: "Not Run",
  PASS: "Pass",
  FAIL: "Fail",
  BLOCKED: "Blocked",
};

// ---------------------------------------------------------------------------
// Parties, people, links, severity
// ---------------------------------------------------------------------------

export const partyLabel = (
  party: Party | null | undefined,
  settings: GovSettingsOn | null | undefined,
): string => {
  if (party === "CLIENT") return settings?.parties?.clientLabel || "Client";
  if (party === "VENDOR") return settings?.parties?.vendorLabel || "Vendor";
  return "—";
};

export const clientLabel = (settings: GovSettingsOn | null | undefined) =>
  settings?.parties?.clientLabel || "Client";
export const vendorLabel = (settings: GovSettingsOn | null | undefined) =>
  settings?.parties?.vendorLabel || "Vendor";

export const RELATION_LABEL: Record<LinkRelation, string> = {
  DEPENDS_ON: "depends on",
  BLOCKS: "blocks",
  RELATED: "related to",
  FIXED_IN: "fixed in",
  FIXES: "fixes",
  TESTED_IN: "tested in",
  TESTS: "tests",
};

export const relationLabel = (relation: string) =>
  (RELATION_LABEL as Record<string, string>)[relation] ?? relation.toLowerCase().replace(/_/g, " ");

export const SEVERITY_LABEL: Record<string, string> = {
  S1: "S1 Critical",
  S2: "S2 Major",
  S3: "S3 Minor",
  S4: "S4 Cosmetic",
};
export const severityLabel = (s: string | null | undefined) => (s ? SEVERITY_LABEL[s] ?? s : "—");

export const ENVIRONMENT_LABEL: Record<string, string> = { DEV: "DEV", QA: "QA", UAT: "UAT", PROD: "PROD" };

export const RELEASE_TYPE_LABEL: Record<string, string> = {
  MAJOR: "Major",
  MINOR: "Minor",
  PATCH: "Patch",
  HOTFIX: "Hotfix",
};
export const releaseTypeLabel = (t: string | null | undefined) => (t ? RELEASE_TYPE_LABEL[t] ?? t : "—");

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/**
 * Date-only fields (dueDate, startDate, testDate, weekEnding, milestones) are
 * stored at 00:00 UTC: show the stored day, never through local time.
 */
export const dateOnly = (iso: string | null | undefined): string =>
  iso ? String(iso).slice(0, 10) : "—";

/** Timestamps (createdAt, comments, history). */
export const dateTime = (iso: string | null | undefined): string =>
  iso ? moment(iso).format("MMM D, YYYY h:mm A") : "—";

export const shortDate = (iso: string | null | undefined): string =>
  iso ? moment(iso).format("MMM D, YYYY") : "—";

/** Minutes → "12.50" hours; null → "—". */
export const hours = (minutes: number | null | undefined): string =>
  typeof minutes === "number" ? (minutes / 60).toFixed(2) : "—";

/** actual − estimated, signed, or "—" when either side is missing. */
export const hoursVariance = (
  actualMinutes: number | null | undefined,
  estimatedMinutes: number | null | undefined,
): string => {
  if (typeof actualMinutes !== "number" || typeof estimatedMinutes !== "number") return "—";
  const diff = (actualMinutes - estimatedMinutes) / 60;
  return `${diff > 0 ? "+" : diff < 0 ? "−" : ""}${Math.abs(diff).toFixed(2)}`;
};

export const yesNo = (v: boolean | null | undefined) => (v ? "Yes" : "No");

/** Linked display IDs of the given kinds (all kinds when none given). */
export const linkedIds = (item: Pick<GovItem, "links">, kinds?: GovKind[]): string[] =>
  (item.links ?? [])
    .filter((l) => !kinds || (l.targetKind && kinds.includes(l.targetKind)))
    .map((l) => l.targetDisplayId)
    .filter((id): id is string => !!id);

export const dash = (v: string | number | null | undefined): string =>
  v === null || v === undefined || v === "" ? "—" : String(v);
